import { AppState } from '@redux/store'
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { Form } from '@typesApp/Forms'
import { ViewName } from '@typesApp/PageFlow'
import { Insurer, InsurerErrorType, ProviderType } from '@typesApp/Insurer'
import { FormChangeState, FormErrorType } from '@typesApp/InsuranceForm'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { connectorSlice } from './connectorSlice'

type FormState = {
  insurer?: Insurer
  insurerError?: InsurerErrorType
  insurerProviderType?: ProviderType
  forms: Form[]
  index: number
  precheck: boolean
  changeState?: FormChangeState
  errors: Record<string, FormErrorType>
}

const initialState: FormState = {
  forms: [],
  index: -1,
  precheck: false,
  errors: {},
}

const REDUCER_PATH = 'forms'

export const formsSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setFormsPrecheck(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        precheck: action.payload,
      }
    },
    setForms(state, action: PayloadAction<Form[]>) {
      return {
        ...state,
        forms: action.payload,
        index: Array.isArray(action.payload) && action.payload.length > 0 ? 0 : -1,
      }
    },
    initLastForm(state) {
      state.index = (state.forms?.length ?? 0) - 1
    },
    resetLastForm(state) {
      state.index = state.index !== -1 ? 0 : -1
    },
    selectInsurer(state, action: PayloadAction<{ insurer: Insurer; forms: Form[] }>) {
      // clear insurer error if selection is different
      if (state.insurer?.id !== action.payload?.insurer?.id) {
        state.insurerError = undefined
      }
      state.insurer = action.payload.insurer
      state.forms = action.payload.forms
    },
    setInsurerProviderType(state, action: PayloadAction<ProviderType>) {
      state.insurerProviderType = action.payload
    },
    resetInsurerProviderType(state) {
      state.insurerProviderType = undefined
    },
    nextForm(state) {
      return {
        ...state,
        index: state.index !== -1 && state.index < state?.forms?.length - 1 ? state.index + 1 : state.index,
      }
    },
    previousForm(state) {
      return {
        ...state,
        index: state.index !== -1 && state.index > 0 ? state.index - 1 : state.index,
      }
    },
    formAtIndex(state, action: PayloadAction<number>) {
      return {
        ...state,
        index: action.payload,
      }
    },
    setFormChangeState(state, action: PayloadAction<FormChangeState>) {
      return {
        ...state,
        changeState: action.payload,
      }
    },
    setFormError(state, action: PayloadAction<{ view: ViewName; error: FormErrorType }>) {
      if (state.errors) {
        state.errors[action.payload.view] = action.payload.error
      } else {
        state.errors = {
          [action.payload.view]: action.payload.error,
        }
      }
    },
    setInsurerError(state, action: PayloadAction<InsurerErrorType>) {
      if (state.insurer) {
        state.insurerError = action.payload
      }
    },
    resetFormError(state, action: PayloadAction<ViewName>) {
      if (state.errors) {
        const errors = { ...state.errors }
        delete errors[action.payload]
        state.errors = errors
      }
    },
    resetFormChangeState(state) {
      return {
        ...state,
        changeState: undefined,
      }
    },
    resetForms(state) {
      return {
        ...state,
        index: state.index !== -1 ? 0 : -1,
        precheck: true,
        changeState: undefined,
        errors: {},
        insurer: undefined,
        insurerError: undefined,
      }
    },
    resetFormsInitialState() {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * Reset selected insurer
     */
    builder.addMatcher(
      isAnyOf(
        connectorSlice.actions.connectorOrderComplete,
        claimRecordsApi.endpoints.createClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.nullifyClaim.matchFulfilled,
        claimRecordsApi.endpoints.submitClaim.matchFulfilled
      ),
      state => {
        state.insurer = undefined
        state.insurerError = undefined
        state.insurerProviderType = undefined
      }
    )

    /**
     * Reset to initial state on session expiry or logout
     */
    builder.addMatcher(
      isAnyOf(connectorSlice.actions.sessionExpired, connectorSlice.actions.connectorLogoutInProgress),
      () => {
        return initialState
      }
    )
  },
})

export const {
  setForms,
  selectInsurer,
  setInsurerProviderType,
  resetInsurerProviderType,
  nextForm,
  previousForm,
  formAtIndex,
  resetForms,
  initLastForm,
  resetLastForm,
  setFormsPrecheck,
  setFormError,
  resetFormError,
  setInsurerError,
  setFormChangeState,
  resetFormChangeState,
  resetFormsInitialState,
} = formsSlice.actions

export const formsPrecheckSelector = (state: AppState): boolean => state.forms.precheck === true
export const formsStateSelector = (state: AppState): FormState => state.forms
export const selectedInsurerSelector = (state: AppState): Insurer | undefined => state.forms.insurer
export const selectedInsurerErrorSelector = (state: AppState): InsurerErrorType | undefined => state.forms.insurerError
export const selectedInsurerProviderTypeSelector = (state: AppState): ProviderType | undefined =>
  state.forms.insurerProviderType
export const formChangeStateSelector = (state: AppState): FormChangeState | undefined => state.forms.changeState
export const formErrorsSelector = (state: AppState): Record<ViewName, FormErrorType> => state.forms.errors
